import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  SearchIcon,
  FilterIcon,
  ViewListIcon,
  ViewGridIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeOffIcon,
  DocumentTextIcon
} from '@heroicons/react/outline';
import { 
  DocumentDownloadIcon
} from '@heroicons/react/solid';
import toastService from '../../../../services/toast';
import * as XLSX from 'xlsx';
import AddExamModal from './components/AddExamModal';
import EditExamModal from './components/EditExamModal';
import DeleteExamModal from './components/DeleteExamModal';
import { examsService } from '../../../../services/exams';
import { usePermissions } from '../../../../contexts/PermissionsContext';

const ExamsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('table'); // 'table' or 'grid'
  const [lastExamCount, setLastExamCount] = useState(3); // تخزين عدد الصفوف السابق للاختبارات

  // جلب بيانات الاختبارات عند تحميل الصفحة
  useEffect(() => {
    const fetchExams = async () => {
      try {
        setLoading(true);
        const data = await examsService.getExams();
        // استجابة API للاختبارات
        // تأكد من أن البيانات مصفوفة وأن حقل course نص بسيط
        if (Array.isArray(data)) {
          // حفظ عدد الاختبارات للاستخدام أثناء التحميل المستقبلي
          setLastExamCount(data.length || 3);
          const formattedData = data.map(exam => ({
            ...exam,
            course: typeof exam.course === 'object' && exam.course ? exam.course.name || 'غير محدد' : exam.course || 'غير محدد'
          }));
          // بيانات الاختبارات بعد التنسيق
          setExams(formattedData);
        } else {
          // خطأ: البيانات ليست مصفوفة
          setExams([]);
        }
      } catch (error) {
        // معالجة خطأ جلب الاختبارات
        toastService.error('حدث خطأ أثناء جلب بيانات الاختبارات');
      } finally {
        setLoading(false);
      }
    };

    fetchExams();
  }, []);
  
  // لا نحتاج للتأثير هنا لأن البحث يتم بشكل فوري من خلال filteredExams المعرف لاحقا في الكود

  const handleExportAllToExcel = () => {
    // تجهيز البيانات للتصدير - فقط المعلومات الظاهرة في الجدول
    const data = exams.map(exam => ({
      'عنوان الاختبار': exam.title,
      'الدورة': exam.course,
      'المدة': `${exam.duration} دقيقة`,
      'الدرجة': `${exam.total_score} درجة`,
      'الحالة': exam.status === 'published' ? 'منشور' : 'مسودة'
    }));

    // إنشاء ورقة عمل Excel
    const ws = XLSX.utils.json_to_sheet(data, { header: ['عنوان الاختبار', 'الدورة', 'المدة', 'الدرجة', 'الحالة'] });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'الاختبارات');
    
    // تحميل الملف
    XLSX.writeFile(wb, 'قائمة-الاختبارات.xlsx');
    toastService.success('تم تصدير قائمة الاختبارات بنجاح');
  };

  const handlePreviewExam = (examId) => {
    window.open(`/admin/dashboard/courses/exams/preview/${examId}`, '_blank');
  };

  const handleExportExamToExcel = async (exam) => {
    try {
      // جلب أسئلة الاختبار
      const questions = await examsService.getExamQuestions(exam.id);
      
      // تجهيز بيانات الاختبار المحدد
      const examInfo = [{
        'عنوان الاختبار': exam.title,
        'الدورة': exam.course,
        'المدة': `${exam.duration} دقيقة`,
        'الدرجة الكلية': `${exam.total_score || exam.totalScore} درجة`,
        'الحالة': exam.status === 'active' ? 'نشط' : 'غير نشط',
        'الوصف': exam.description || '',
      }];
      
      // تجهيز بيانات الأسئلة
      let questionsData = [];
      questions.forEach((q, index) => {
        // استخراج الإجابات والإجابة الصحيحة
        const answers = q.answers || [];
        // استخراج الإجابة الصحيحة
        const correctAnswerIndex = answers.findIndex(a => a.is_correct === true || a.isCorrect === true);
        
        // حساب درجة السؤال = الدرجة الكلية / عدد الأسئلة
        const questionScore = exam.total_score / questions.length;
        
        // التأكد من وجود 4 إجابات
        const normalizedAnswers = Array(4).fill('').map((_, i) => answers[i]?.text || '');
        
        // إضافة السؤال وإجاباته
        questionsData.push({
          'رقم السؤال': index + 1,
          'نص السؤال': q.text || '',
          'الدرجة': questionScore,
          'الإجابة': '',
          'الإجابة الصحيحة': ''
        });

        // إضافة الإجابات الأربعة
        normalizedAnswers.forEach((answer, answerIndex) => {
          questionsData.push({
            'رقم السؤال': '',
            'نص السؤال': '',
            'الدرجة': '',
            'الإجابة': answer,
            'الإجابة الصحيحة': answerIndex === correctAnswerIndex ? '✓' : ''
          });
        });
      });
      
      // إنشاء ورقة عمل Excel للاختبار
      const wsExamInfo = XLSX.utils.json_to_sheet(examInfo);
      
      // إنشاء ورقة عمل Excel للأسئلة
      const wsQuestions = XLSX.utils.json_to_sheet(questionsData);
      
      // إنشاء كتاب Excel
      const wb = XLSX.utils.book_new();
      
      // إضافة الأوراق إلى الكتاب
      XLSX.utils.book_append_sheet(wb, wsExamInfo, 'معلومات الاختبار');
      XLSX.utils.book_append_sheet(wb, wsQuestions, 'أسئلة الاختبار');
      
      // تحميل الملف
      XLSX.writeFile(wb, `اختبار-${exam.title}.xlsx`);
      toastService.success('تم تصدير الاختبار بنجاح');
    } catch (error) {
      // معالجة خطأ تصدير الاختبار
      toastService.error('حدث خطأ أثناء تصدير الاختبار');
    }
  };

  const handleAddExam = async (examData) => {
    try {
      const newExam = await examsService.createExam(examData);
      setExams([...exams, newExam]);
      setShowAddModal(false);
      toastService.success('تم إضافة الاختبار بنجاح');
    } catch (error) {
      // معالجة خطأ إضافة الاختبار
      toastService.error('حدث خطأ أثناء إضافة الاختبار');
    }
  };

  const handleEditExam = async (examData) => {
    try {
      await examsService.updateExam(examData.id, examData);
      const updatedExams = exams.map(exam => 
        exam.id === examData.id ? examData : exam
      );
      setExams(updatedExams);
      setShowEditModal(false);
      toastService.success('تم تحديث الاختبار بنجاح');
    } catch (error) {
      // معالجة خطأ تحديث الاختبار
      toastService.error('حدث خطأ أثناء تحديث الاختبار');
    }
  };

  // دالة لحذف اختبار (حذف فعلي)
  const handleDeleteExam = async () => {
    if (!selectedExam) return;
    
    try {
      // استخدام الحذف الفعلي (force=true)
      await examsService.deleteExam(selectedExam.id, true);
      setExams(exams.filter(exam => exam.id !== selectedExam.id));
      setShowDeleteModal(false);
      toastService.success('تم حذف الاختبار نهائياً بنجاح');
    } catch (error) {
      // معالجة خطأ حذف الاختبار
      toastService.error('حدث خطأ أثناء حذف الاختبار');
    }
  };

  // دالة لتبديل حالة الاختبار (نشر/إخفاء)
  const handleToggleExamStatus = async (exam) => {
    try {
      const newStatus = exam.status === 'published' ? 'draft' : 'published';
      await examsService.toggleExamStatus(exam.id, newStatus);
      
      // تحديث القائمة مباشرة بدون الحاجة إلى إعادة التحميل
      const updatedExams = exams.map(e => 
        e.id === exam.id ? { ...e, status: newStatus } : e
      );
      setExams(updatedExams);
      
      const message = newStatus === 'published' ? 'تم نشر الاختبار بنجاح' : 'تم إخفاء الاختبار بنجاح';
      toastService.success(message);
    } catch (error) {
      // معالجة خطأ تغيير حالة الاختبار
      toastService.error('حدث خطأ أثناء تغيير حالة الاختبار');
    }
  };

  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         exam.course.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <div className="p-6">
      {/* بطاقة العنوان والبحث والأيقونات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg mb-6">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">قائمة الاختبارات</h2>
          <div className="flex gap-4">
            {hasPermission('create_exam') && (
              <div className="relative group overflow-visible">
                <PlusIcon 
                  className="h-6 w-6 text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400 cursor-pointer transition-colors duration-200"
                  onClick={() => navigate('create')}
                  aria-hidden="true"
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                  إضافة اختبار جديد
                </div>
              </div>
            )}
            
            {hasPermission('view_exams') && (
              <div className="relative group overflow-visible">
                <DocumentDownloadIcon 
                  className="h-6 w-6 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 cursor-pointer transition-colors duration-200"
                  onClick={handleExportAllToExcel}
                  aria-hidden="true"
                />
                <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                  تصدير كل الاختبارات
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="p-6">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">البحث عن اختبار</label>
            <div className="relative">
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" aria-hidden="true" />
              </div>
              <input
                type="text"
                placeholder="البحث عن اختبار..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-base pr-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* بطاقة جدول الاختبارات */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        {loading ? (
          <div className="overflow-x-auto w-full">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="w-4/12 px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></span>
                  </th>
                  <th scope="col" className="w-3/12 px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></span>
                  </th>
                  <th scope="col" className="w-1/12 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></span>
                  </th>
                  <th scope="col" className="w-1/12 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></span>
                  </th>
                  <th scope="col" className="w-1/12 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></span>
                  </th>
                  <th scope="col" className="w-[120px] px-2 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span className="animate-pulse block h-5 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {[...Array(lastExamCount)].map((_, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-center">
                      <div className="animate-pulse h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></div>
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-center">
                      <div className="animate-pulse h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <div className="animate-pulse h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                      <div className="animate-pulse h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4 mx-auto"></div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <div className="animate-pulse h-6 bg-gray-300 dark:bg-gray-600 rounded w-[70px] mx-auto"></div>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-3">
                        <div className="animate-pulse h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded-md"></div>
                        <div className="animate-pulse h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded-md"></div>
                        <div className="animate-pulse h-8 w-8 bg-gray-300 dark:bg-gray-600 rounded-md"></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : filteredExams.length === 0 ? (
          <div className="text-center p-8 text-gray-500 dark:text-gray-400">
            لا توجد اختبارات متاحة
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="w-4/12 px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>عنوان الاختبار</span>
                  </th>
                  <th scope="col" className="w-3/12 px-6 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>الدورة</span>
                  </th>
                  <th scope="col" className="w-1/12 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>المدة</span>
                  </th>
                  <th scope="col" className="w-1/12 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>الدرجة</span>
                  </th>
                  <th scope="col" className="w-1/12 px-4 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>الحالة</span>
                  </th>
                  <th scope="col" className="w-[80px] px-2 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>معاينة</span>
                  </th>
                  <th scope="col" className="w-[120px] px-2 py-3 text-sm font-semibold text-gray-900 dark:text-gray-100 text-center">
                    <span>إجراءات</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredExams.map((exam) => (
                  <tr key={exam.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150">
                    <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white text-center">
                      {exam.title}
                    </td>
                    <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {exam.course}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {exam.duration} دقيقة
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300 text-center">
                      {exam.total_score} درجة
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span className={`inline-flex min-w-[70px] justify-center px-2 py-1 text-xs font-semibold rounded-md ${
                        exam.status === 'published'
                          ? 'bg-green-100 text-green-800 dark:bg-green-200 dark:text-green-900'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-200 dark:text-yellow-900'
                      }`}>
                        {exam.status === 'published' ? 'منشور' : 'مسودة'}
                      </span>
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-center">
                      {hasPermission('view_exams') && (
                        <div className="flex items-center justify-center">
                          <div className="relative group overflow-visible">
                            <button
                              onClick={() => handlePreviewExam(exam.id)}
                              className="text-purple-600 hover:text-purple-700 dark:text-purple-500 dark:hover:text-purple-400 transition-colors duration-200"
                            >
                              <DocumentTextIcon className="w-5 h-5" aria-hidden="true" />
                            </button>
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                              معاينة الاختبار
                            </div>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap text-gray-500 dark:text-gray-300 text-center">
                      <div className="flex items-center justify-center gap-3">
                        {hasPermission('edit_exam') && (
                          <div className="relative group overflow-visible">
                            <button
                              onClick={() => navigate(`/admin/dashboard/courses/exams/edit/${exam.id}`)}
                              className="text-gray-400 dark:text-gray-500 hover:text-blue-700 dark:hover:text-blue-400 transition-colors duration-200"
                            >
                              <PencilIcon className="w-5 h-5" aria-hidden="true" />
                            </button>
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                              تعديل الاختبار
                            </div>
                          </div>
                        )}
                        {hasPermission('publish_exam') && (
                          <div className="relative group overflow-visible">
                            <button
                              onClick={() => handleToggleExamStatus(exam)}
                              className="transition-colors duration-200"
                            >
                              {exam.status === 'published' ? 
                                <EyeIcon className="w-5 h-5 text-green-500" aria-hidden="true" /> : 
                                <EyeOffIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400" aria-hidden="true" />}
                            </button>
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                              {exam.status === 'published' ? 'إخفاء الاختبار' : 'نشر الاختبار'}
                            </div>
                          </div>
                        )}
                        {hasPermission('view_exams') && (
                          <>
                            <div className="relative group overflow-visible">
                              <button
                                onClick={() => handleExportExamToExcel(exam)}
                                className="text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400 transition-colors duration-200"
                              >
                                <DocumentDownloadIcon className="w-5 h-5" aria-hidden="true" />
                              </button>
                              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full right-auto left-0 -translate-y-1 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                                تصدير الاختبار إلى Excel
                              </div>
                            </div>
                          </>
                        )}
                        {hasPermission('delete_exam') && (
                          <div className="relative group overflow-visible">
                            <button
                              onClick={() => {
                                setSelectedExam(exam);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400 transition-colors duration-200"
                            >
                              <TrashIcon className="w-5 h-5" aria-hidden="true" />
                            </button>
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full right-auto left-0 -translate-y-1 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                              حذف الاختبار
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* المودالز */}
      <AddExamModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddExam}
      />

      <EditExamModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onEdit={handleEditExam}
        exam={selectedExam}
      />

      <DeleteExamModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDeleteExam}
        examTitle={selectedExam?.title}
      />
    </div>
  );
};

export default ExamsPage;
